import { formatItemValue, getDimensionsFromItemsMap, getMetricsFromItemsMap, getTableCalculationsFromItemsMap, isField, isHexCodeColor, isMetric, isNumericItem, isTableCalculation, PieChartLegendLabelMaxLengthDefault, PieChartLegendPositionDefault, } from '@lightdash/common';
import { isEmpty, omitBy, pick, pickBy } from 'es-toolkit/compat';
/** The metric the pie slices are sized by, when `metricId` names a metric or table calculation. */
export const getPieSelectedMetric = (itemsMap, metricId) => {
    if (!itemsMap || !metricId)
        return undefined;
    const item = itemsMap[metricId];
    if ((isField(item) && isMetric(item)) || isTableCalculation(item))
        return item;
    return undefined;
};
/**
 * Drops group fields that are no longer in the results, falling back to the
 * first dimension when none is left.
 */
export const repairPieGroupFieldIds = ({ groupFieldIds, dimensionIds, pendingDimensionIds, }) => {
    const newGroupFieldIds = groupFieldIds.filter((id) => dimensionIds.includes(id) || (!!id && pendingDimensionIds.has(id)));
    const firstDimensionId = dimensionIds[0];
    if (newGroupFieldIds.length === 0 && firstDimensionId) {
        return [firstDimensionId];
    }
    return newGroupFieldIds;
};
/**
 * Keeps the selected metric when it is still available, follows a renamed
 * table calculation, and otherwise falls back to the first numeric metric.
 */
export const repairPieMetricId = ({ metricId, allNumericMetricIds, pendingMetricIds, tableCalculationsMetadata, }) => {
    if (metricId &&
        (allNumericMetricIds.includes(metricId) ||
            pendingMetricIds.has(metricId)))
        return metricId;
    /**
     * When table calculations update, their name changes, so we need to update the selected fields
     * If the selected field is a table calculation with the old name in the metadata, set it to the new name
     */
    if (tableCalculationsMetadata) {
        const metricTcIndex = tableCalculationsMetadata.findIndex((tc) => tc.oldName === metricId);
        if (metricTcIndex !== -1) {
            return tableCalculationsMetadata[metricTcIndex].name;
        }
    }
    return allNumericMetricIds[0] ?? null;
};
/** Whether any slice overrides the given value option. */
export const isPieValueOptionOverridden = (groupValueOptionOverrides, option) => Object.values(groupValueOptionOverrides).some((value) => value[option] !== undefined);
/**
 * Aggregates the result rows into one slice per group: the group name joins
 * the formatted group field values, the value sums the metric, and slices
 * come out largest first.
 */
export const getPieChartData = ({ resultsData, groupFieldIds, metricId, selectedMetric, parameters, }) => {
    if (!metricId ||
        !selectedMetric ||
        !resultsData ||
        resultsData.rows.length === 0 ||
        !groupFieldIds ||
        groupFieldIds.length === 0) {
        return [];
    }
    const isMetricPresentInResults = resultsData?.rows.some((r) => r[metricId]);
    if (!isMetricPresentInResults) {
        return [];
    }
    // A null-prototype object rather than a Map: Object.entries orders
    // integer-like group names first, and the tie order of the value sort
    // below depends on it.
    const groups = Object.create(null);
    for (const row of resultsData.rows) {
        // A row without the metric cell has nothing to add to a slice
        const metricCell = row[metricId];
        if (!metricCell)
            continue;
        const name = groupFieldIds
            .map((groupFieldId) => row[groupFieldId]?.value?.formatted)
            .filter(Boolean)
            .join(' - ');
        const value = Number(metricCell.value.raw);
        groups[name] ?? (groups[name] = { value: 0, rows: [] });
        groups[name].value += value;
        groups[name].rows.push(row);
    }
    return Object.entries(groups)
        .map(([name, { value, rows }]) => ({
        name,
        value,
        meta: {
            value: {
                formatted: formatItemValue(selectedMetric, value, false, parameters, resultsData?.resolvedTimezone),
                raw: value,
            },
            rows,
        },
    }))
        .sort((a, b) => b.value - a.value);
};
/** The group labels in the order the user sorted them, or by value when no override applies. */
export const getSortedPieGroupLabels = (groupSortOverrides, groupLabels) => {
    const availableSortedOverrides = groupSortOverrides.filter((label) => groupLabels.includes(label));
    return availableSortedOverrides.length > 0
        ? availableSortedOverrides
        : groupLabels;
};
/** The palette color each group gets by position, shown in the editor as the default swatch. */
export const getPieGroupColorDefaults = (groupLabels, colorPalette) => Object.fromEntries(groupLabels.map((name, index) => {
    return [name, colorPalette[index % colorPalette.length]];
}));
/** The saved pie config from the editor state, with overrides for missing groups pruned. */
export const buildValidPieConfig = ({ groupFieldIds, metricId, isDonut, valueLabel, showValue, showPercentage, valueLabelColor, groupLabels, groupLabelOverrides, groupColorOverrides, groupValueOptionOverrides, groupSortOverrides, showLegend, legendPosition, legendMaxItemLength, }) => ({
    groupFieldIds,
    metricId: metricId ?? undefined,
    isDonut,
    valueLabel,
    showValue,
    showPercentage,
    valueLabelColor,
    groupLabelOverrides: pick(groupLabelOverrides, groupLabels),
    groupColorOverrides: pickBy(pick(groupColorOverrides, groupLabels), isHexCodeColor),
    groupValueOptionOverrides: omitBy(pick(groupValueOptionOverrides, groupLabels), isEmpty),
    groupSortOverrides: groupSortOverrides.filter((label) => groupLabels.includes(label)),
    showLegend,
    legendPosition,
    legendMaxItemLength,
});
/** The group and metric pools the pie editor picks from, as the frontend derives them. */
export const getPieFieldPools = (itemsMap) => {
    const metrics = getMetricsFromItemsMap(itemsMap ?? {}, isNumericItem);
    const tableCalculations = getTableCalculationsFromItemsMap(itemsMap);
    return {
        dimensions: getDimensionsFromItemsMap(itemsMap ?? {}),
        numericMetrics: { ...metrics, ...tableCalculations },
    };
};
/**
 * Resolves a saved pie config against query results, the way the explorer
 * does when it first mounts a chart: drops group fields and metrics that
 * left the results, picks defaults, aggregates the slices and prunes
 * overrides for groups that are gone.
 *
 * The frontend's `usePieChartConfig` runs the same steps inside its effects
 * and memos; this is the one-shot equivalent for headless rendering. Without
 * results the config is returned as the hook holds it before loading: no
 * repairs and no slices.
 */
export const resolvePieChartConfig = ({ chartConfig: pieChartConfig, resultsData, itemsMap, colorPalette, dimensions, numericMetrics, tableCalculationsMetadata, parameters, unsavedMetricQuery, }) => {
    const pools = getPieFieldPools(itemsMap);
    const dimensionIds = Object.keys(dimensions ?? pools.dimensions);
    const allNumericMetricIds = Object.keys(numericMetrics ?? pools.numericMetrics);
    const isLoading = !resultsData;
    // The pools above only know the last run; a field just added from the
    // config picker must not be dropped before its results land.
    const pendingDimensionIds = new Set(unsavedMetricQuery?.dimensions);
    const pendingMetricIds = new Set(unsavedMetricQuery?.metrics);
    let groupFieldIds = pieChartConfig?.groupFieldIds ?? [];
    if (!isLoading && dimensionIds.length > 0) {
        groupFieldIds = repairPieGroupFieldIds({
            groupFieldIds,
            dimensionIds,
            pendingDimensionIds,
        });
    }
    let metricId = pieChartConfig?.metricId ?? null;
    if (!isLoading && allNumericMetricIds.length > 0) {
        // Twice: a renamed table calculation is first followed to its new
        // name, then dropped if that name is not in the pool, which is what
        // the editor settles on over two renders.
        const repair = (id) => repairPieMetricId({
            metricId: id,
            allNumericMetricIds,
            pendingMetricIds,
            tableCalculationsMetadata,
        });
        metricId = repair(repair(metricId));
    }
    const isDonut = pieChartConfig?.isDonut ?? true;
    const valueLabel = pieChartConfig?.valueLabel ?? 'hidden';
    const showValue = pieChartConfig?.showValue ?? false;
    const showPercentage = pieChartConfig?.showPercentage ?? true;
    const valueLabelColor = pieChartConfig?.valueLabelColor;
    const groupLabelOverrides = pieChartConfig?.groupLabelOverrides ?? {};
    const groupColorOverrides = pieChartConfig?.groupColorOverrides ?? {};
    const groupValueOptionOverrides = pieChartConfig?.groupValueOptionOverrides ?? {};
    const groupSortOverrides = pieChartConfig?.groupSortOverrides ?? [];
    const showLegend = pieChartConfig?.showLegend ?? true;
    const legendPosition = pieChartConfig?.legendPosition ?? PieChartLegendPositionDefault;
    const legendMaxItemLength = pieChartConfig?.legendMaxItemLength ??
        PieChartLegendLabelMaxLengthDefault;
    const selectedMetric = getPieSelectedMetric(itemsMap, metricId);
    const data = getPieChartData({
        resultsData,
        groupFieldIds,
        metricId,
        selectedMetric,
        parameters,
    });
    const groupLabels = data.map(({ name }) => name);
    const sortedGroupLabels = getSortedPieGroupLabels(groupSortOverrides, groupLabels);
    const groupColorDefaults = getPieGroupColorDefaults(groupLabels, colorPalette);
    const validConfig = buildValidPieConfig({
        groupFieldIds,
        metricId,
        isDonut,
        valueLabel,
        showValue,
        showPercentage,
        valueLabelColor,
        groupLabels,
        groupLabelOverrides,
        groupColorOverrides,
        groupValueOptionOverrides,
        groupSortOverrides,
        showLegend,
        legendPosition,
        legendMaxItemLength,
    });
    return {
        validConfig,
        metricId,
        selectedMetric,
        groupFieldIds,
        data,
        groupLabels,
        sortedGroupLabels,
        groupColorDefaults,
    };
};
